import { LayoutDashboard, Sparkles } from "lucide-react";
import { cn } from "@/lib/utils";
import { useLocale } from "@/hooks/useLocale";
import { useBuilderUiVersion, type BuilderUiVersion } from "@/hooks/useBuilderUiVersion";

interface BuilderVersionSwitchProps {
  /** Optional controlled value/handler. When omitted, the component manages its own state via the hook. */
  value?: BuilderUiVersion;
  onChange?: (next: BuilderUiVersion) => void;
  className?: string;
}

/**
 * Segmented two-state toggle that switches the Builder UI between V1 (current
 * builder) and V2 (new canvas). Persists per-user via useBuilderUiVersion when
 * used uncontrolled.
 */
export function BuilderVersionSwitch({ value, onChange, className }: BuilderVersionSwitchProps) {
  const { t } = useLocale();
  const { version, setVersion } = useBuilderUiVersion();

  const current = value ?? version;
  const select = (next: BuilderUiVersion) => {
    if (next === current) return;
    if (onChange) onChange(next);
    else void setVersion(next);
  };

  const baseBtn =
    "inline-flex items-center gap-1.5 h-7 px-2 sm:px-2.5 text-[11px] sm:text-xs font-medium rounded-md transition-colors";
  const active = "bg-background text-foreground shadow-sm";
  const inactive = "text-muted-foreground hover:text-foreground";

  return (
    <div
      role="tablist"
      aria-label={t.wb_ui_version_label}
      className={cn(
        "inline-flex items-center gap-0.5 rounded-md border border-border bg-muted/60 p-0.5 shrink-0",
        className,
      )}
    >
      <button
        type="button"
        role="tab"
        aria-selected={current === "v1"}
        onClick={() => select("v1")}
        className={cn(baseBtn, current === "v1" ? active : inactive)}
      >
        <LayoutDashboard className="w-3 h-3" />
        <span>{t.wb_ui_version_v1}</span>
      </button>
      <button
        type="button"
        role="tab"
        aria-selected={current === "v2"}
        onClick={() => select("v2")}
        className={cn(baseBtn, current === "v2" ? active : inactive)}
      >
        <Sparkles className="w-3 h-3" />
        <span>{t.wb_ui_version_v2}</span>
      </button>
    </div>
  );
}
